import type { DestinationsContainer } from '@elara/client-core/containers/DestinationsContainer';
import type { FlightOptionsContainer } from '@elara/client-core/containers/FlightOptionsContainer';
import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';
import type { PreviewType } from '@elara/client-core/lib/route-utils';
import type { Destination } from '@elara/client-core/models/DestinationModel';
import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { Hotel } from '@elara/client-core/models/HotelModel';
import type { Place } from '@elara/client-core/models/PlaceModel';
import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';

import { roomShortlistMetadata } from '@elara/client-core/lib/room-utils';
import { shouldLetBrowserHandleClick } from '@elara/client-core/lib/route-utils';
import { parseDocumentId } from '@elara/client-core/lib/utils';

import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useRouterState, useShortlistState, useTripsState } from '#states/hooks.js';
import { ShortlistButton } from '#ui';
import { DestinationCard } from '#ui/cards/DestinationCard.js';
import { FlightCard } from '#ui/cards/FlightCard.js';
import { HotelCard } from '#ui/cards/HotelCard.js';
import { PlaceCard } from '#ui/cards/PlaceCard.js';
import { RoomCard } from '#ui/cards/RoomCard.js';
import { useOpenGuide } from '#ui/guides/guide-scope.js';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '#ui/hover-card.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';

export interface PreviewLinkProps {
  previewType: PreviewType;
  id: string;
  displayShortlistIcon?: boolean;
  /** Holds the popover open instead of opening it on hover: for reviewing every popover at once in Storybook. */
  open?: boolean;
  children: React.ReactNode;
}

/** Normalized preview data for destination, hotel, room, or place */
function getPreviewInfo(
  previewType: PreviewType,
  data: Destination | Hotel | RoomOptionModel | Place,
): {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  imageUrls: string[];
  location?: { label?: string; address?: string; coordinates?: { lat: number; lng: number } };
} {
  if (previewType === 'destination') {
    const d = data as Destination;
    return {
      title: d.displayName,
      subtitle: d.longDescription ?? undefined,
      imageUrl: d.imageUrl,
      imageUrls: d.imageUrls ?? [],
      location: { label: d.address, coordinates: d.coordinates },
    };
  }
  if (previewType === 'room') {
    const r = data as RoomOptionModel;
    const imageUrls = r.images?.map((img) => img.url).filter(Boolean) ?? [];
    return {
      title: r.displayName,
      subtitle: r.hotel?.name,
      imageUrl: r.images?.[0]?.url,
      imageUrls,
      location: r.hotel?.formattedAddress ? { address: r.hotel.formattedAddress } : undefined,
    };
  }
  if (previewType === 'place') {
    const p = data as Place;
    const imageUrls = p.allImages?.map((img) => img.url).filter(Boolean) ?? [];
    return {
      title: p.displayName,
      subtitle: p.primaryCategory ?? undefined,
      imageUrl: p.allImages?.[0]?.url,
      imageUrls,
      location:
        p.coordinates?.lat != null && p.coordinates?.lng != null
          ? {
              address: p.formattedAddress ?? p.shortAddress,
              coordinates: { lat: p.coordinates.lat, lng: p.coordinates.lng },
            }
          : p.formattedAddress
            ? { address: p.formattedAddress }
            : undefined,
    };
  }
  const h = data as Hotel;
  return {
    title: h.name ?? 'Hotel',
    subtitle: h.description ?? h.formattedAddress,
    imageUrl: h.imageUrl,
    imageUrls: h.imageUrls ?? [],
    location: { address: h.formattedAddress },
  };
}

/**
 * A component that renders preview links for destinations and hotels.
 * Validates that the ID exists in Firestore before rendering as a clickable link.
 * If the ID is invalid (hallucinated), renders plain text instead.
 * On hover, shows a tooltip with image, info, and shortlist button.
 */
export function PreviewLink({ previewType, id, displayShortlistIcon = true, open, children }: PreviewLinkProps) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  // Inside a guide, a preview link follows on from it (Back returns); in chat it opens a new guide.
  const openGuide = useOpenGuide();
  const routerState = useRouterState();
  const shortlistState = useShortlistState();
  const { features } = useApp('features');
  const { destinations, hotels, roomOptions, flightOptions, places } = features as {
    destinations: DestinationsContainer;
    hotels: HotelsContainer;
    roomOptions: RoomOptionsContainer;
    flightOptions: FlightOptionsContainer;
    places: PlacesContainer;
  };

  const docId = parseDocumentId(id) ?? id;

  useRefreshPlace(previewType === 'place' ? docId : undefined);

  // Room and flight previews require a current trip (their docs live under trips/{tripId}/...)
  if ((previewType === 'room' || previewType === 'flight') && !tripId) {
    return <span>{children}</span>;
  }

  // Fetch destination, hotel, room, or place from Firestore to validate it exists
  const handle =
    previewType === 'destination'
      ? destinations.ClientDestination.get({ id: docId })
      : previewType === 'hotel'
        ? hotels.ClientHotel.get({ id: docId })
        : previewType === 'flight'
          ? flightOptions.ClientFlightOption.get({ tripId: tripId!, id: docId })
          : previewType === 'place'
            ? places.ClientPlace.get({ id: docId })
            : roomOptions.ClientRoomOption.get({ tripId: tripId!, id: docId });

  // While loading, show plain text to avoid flash of broken link
  if (!handle.isReady) {
    return <span>{children}</span>;
  }

  // If snapshot failed (permission, network, etc.) or document doesn't exist (hallucinated ID), show plain text
  if (handle.error ?? !handle.data) {
    return <span>{children}</span>;
  }

  const handleClick = (e: React.MouseEvent) => {
    // Let browser handle modifier clicks (Cmd+Click, Ctrl+Click, etc.) to open in new tab
    if (shouldLetBrowserHandleClick(e)) {
      return;
    }

    e.preventDefault();
    openGuide(previewType, docId);
  };

  // Build href for accessibility (allows right-click open in new tab, etc.)
  // Preserves existing query parameters and hash fragments
  const href = routerState.buildUrlWithHashParams({ preview: previewType, id: docId });

  const isShortlisted = shortlistState.isShortlisted(previewType, docId);
  const preview = getPreviewInfo(previewType, handle.data as Destination | Hotel | RoomOptionModel | Place);

  const linkContent = (
    <>
      {children}
      {displayShortlistIcon && isShortlisted && (
        <MaterialIcon
          icon="favorite"
          filled={true}
          className="text-destructive ml-0.5 align-middle -mt-0.5 preview-shortlisted-icon"
          size="auto"
        />
      )}
    </>
  );

  // `icon-xs`, because the heart now sits ON the cover (see `DetailCardLayout`, `lg`) and this popover
  // is 320px wide — its cover column is barely 115px, where a 40px target takes over the photo.
  const actions = (
    <ShortlistButton itemType={previewType} referenceId={docId} title={preview.title} variant="icon" size="icon-xs" />
  );

  return (
    <HoverCard openDelay={300} closeDelay={100} open={open}>
      <HoverCardTrigger asChild>
        <a href={href} onClick={handleClick} className="group inline hover:no-underline group-hover:underline">
          {linkContent}
        </a>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent side="top" align="center" className="w-80 overflow-hidden p-0 hidden md:block">
          {previewType === 'destination' && (
            <DestinationCard
              destination={handle.data as Destination}
              size="lg"
              layout="row"
              actions={actions}
              href={href}
              onClick={handleClick}
            />
          )}
          {previewType === 'hotel' && (
            <HotelCard
              hotel={handle.data as Hotel}
              size="lg"
              layout="row"
              actions={actions}
              href={href}
              onClick={handleClick}
            />
          )}
          {previewType === 'flight' && (
            <FlightCard
              flightOption={handle.data as FlightOptionModel}
              actions={actions}
              showPriceStatus={false}
              href={href}
              onClick={handleClick}
              // The flight card's content ends on its price row with no padding under it; in a popover
              // that row sat on the edge.
              className="cursor-pointer pb-3"
            />
          )}
          {previewType === 'room' && (
            <RoomCard
              room={handle.data as RoomOptionModel}
              size="lg"
              layout="row"
              actions={
                <ShortlistButton
                  itemType="room"
                  referenceId={docId}
                  title={preview.title}
                  // The room, not a rate, priced for the party it was searched for — as the chat card's heart saves it.
                  metadata={roomShortlistMetadata(handle.data as RoomOptionModel)}
                  variant="icon"
                  size="icon-xs"
                />
              }
              href={href}
              onClick={handleClick}
            />
          )}
          {previewType === 'place' && (
            <PlaceCard
              place={handle.data as Place}
              size="lg"
              layout="row"
              actions={actions}
              href={href}
              onClick={handleClick}
            />
          )}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
